import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Drop, CreditCard, Crown, ArrowRight, Timer, ShieldCheck, CarSimple, ScanSmiley, Gift, Sparkle } from '@phosphor-icons/react';
import axios from 'axios';
import LanguageSwitch from '../components/LanguageSwitch';
import WeeklyWashCounter from '../components/WeeklyWashCounter';
import SubscriptionExpiryGuard from '../components/SubscriptionExpiryGuard';
import DetailingUpsellBanner from '../components/DetailingUpsellBanner';
import StreakBadge from '../components/StreakBadge';
import PremiumServicesSection from '../components/PremiumServicesSection';
import { BRAND, BRAND_ASSETS } from '../config/programs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const { user } = useAuth();
  const { t, tn, fmtDate } = useLanguage();
  const [vipStatus, setVipStatus] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);  // Iter17 Task 5 — multi-plate
  const subscription = subscriptions[0] || null;  // back-compat for elsewhere

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vipRes, subsRes] = await Promise.all([
          axios.get(`${API}/vip/status`, { withCredentials: true }),
          axios.get(`${API}/subscriptions/my-all`, { withCredentials: true })
        ]);
        setVipStatus(vipRes.data);
        setSubscriptions(Array.isArray(subsRes.data) ? subsRes.data : []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  const benefits = [
    { icon: Timer, text: t('washIn5Min'), color: BRAND.primary },
    { icon: ShieldCheck, text: t('noScratches'), color: BRAND.primary },
    { icon: CarSimple, text: t('stayInCar'), color: BRAND.primary },
    { icon: ScanSmiley, text: t('plateRecognitionShort'), color: BRAND.secondary },
  ];

  const quickActions = [
    { path: '/programs', icon: Drop, label: t('buyWash'), color: BRAND.primary, primary: true },
    { path: '/subscriptions', icon: CreditCard, label: t('subscribe'), color: BRAND.secondary },
    { path: '/referrals', icon: Gift, label: t('inviteFriends'), color: BRAND.gold },
  ];

  // Iter20 Task 8 — render subscription window (created → expires) localised.
  const fmtSubDate = (iso) => fmtDate(iso);

  const progressPercent = vipStatus ? (vipStatus.progress_to_next_wash / (vipStatus.target || 7000)) * 100 : 0;
  const isCloseToReward = progressPercent >= 60;

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom">
      <SubscriptionExpiryGuard />
      {/* Header with Logo */}
      <div className="flex items-center justify-between p-4 pt-5">
        <div className="flex items-center gap-3">
          {/* Logo with intense Tiffany contour glow */}
          <div className="relative w-14 h-14 flex items-center justify-center">
            {/* Wide soft outer halo */}
            <div
              className="absolute inset-[-30%] rounded-full blur-2xl opacity-90 animate-pulse-slow pointer-events-none"
              style={{
                background: `radial-gradient(circle, ${BRAND.primary} 0%, ${BRAND.primaryGlow} 35%, transparent 70%)`,
              }}
            />
            {/* Mid halo */}
            <div
              className="absolute inset-[-10%] rounded-full blur-md opacity-80 pointer-events-none"
              style={{
                background: `radial-gradient(circle, ${BRAND.primary} 0%, transparent 65%)`,
              }}
            />
            <img
              src={BRAND_ASSETS.logo}
              alt="AutoSpa5"
              className="w-14 h-14 relative z-10 object-contain"
              style={{
                filter:
                  `drop-shadow(0 0 6px ${BRAND.primary}) ` +
                  `drop-shadow(0 0 14px ${BRAND.primary}) ` +
                  `drop-shadow(0 0 22px ${BRAND.primaryGlow})`,
              }}
              data-testid="logo"
            />
          </div>
          <div>
            <span className="text-lg font-bold tracking-tight font-['Outfit'] text-white">AutoSpa5</span>
            <p className="text-[10px] text-white/40 -mt-0.5">CAR WASH</p>
          </div>
        </div>
        <LanguageSwitch />
      </div>

      {/* Hero Section — Fireart greeting line + bold slogan */}
      <motion.div
        className="px-5 pt-3 pb-1"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#0ABAB5] font-semibold mb-1">
          {t('welcome')} · {user?.name?.split(' ')[0] || 'Guest'}
        </p>
        <h1 className="text-3xl font-bold tracking-tight font-['Outfit'] leading-[1.05] text-white">
          {t('mainSlogan')}
        </h1>
        <WeeklyWashCounter />
      </motion.div>

      {/* Primary CTA - Start Wash */}
      <motion.div
        className="px-5 mt-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <Link
          to="/programs"
          className="block w-full rounded-3xl overflow-hidden relative group"
          data-testid="hero-start-btn"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#0ABAB5] via-[#089994] to-[#067a76]" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_3s_infinite]" />
          <div className="relative flex items-center justify-between p-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkle size={14} weight="fill" className="text-white/85" />
                <p className="text-white/85 text-[10px] font-semibold uppercase tracking-[0.18em]">AutoSpa5</p>
              </div>
              <p className="text-white text-[28px] font-bold font-['Outfit'] leading-none">{t('startNow')}</p>
              <p className="text-white/70 text-xs mt-2">{t('washIn5Min')}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:translate-x-1 transition-transform">
              <ArrowRight size={26} weight="bold" className="text-white" />
            </div>
          </div>
        </Link>
      </motion.div>

      {/* Benefits — Fireart clean list with thin borders */}
      <motion.div
        className="px-5 mt-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
      >
        <div className="grid grid-cols-2 gap-2.5">
          {benefits.map((benefit) => (
            <div
              key={benefit.text}
              className="flex items-center gap-2.5 p-3.5 rounded-2xl bg-[#0a0a0a] border border-white/[0.06] hover:border-white/15 transition-colors"
            >
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${benefit.color}15` }}
              >
                <benefit.icon size={17} weight="duotone" style={{ color: benefit.color }} />
              </div>
              <span className="text-[11px] text-white/75 leading-tight">{benefit.text}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* VIP Card - Premium Design */}
      {vipStatus && (
        <motion.div
          className="mx-4 mt-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="relative rounded-3xl overflow-hidden">
            {/* Gold Border Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/30 via-[#B8860B]/20 to-[#FFD700]/30 rounded-3xl" />
            
            <div className="relative m-[1px] rounded-[22px] bg-gradient-to-br from-[#0a0a0a] to-[#050505] p-5">
              {/* VIP Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_4s_infinite]" />
              
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                      <Crown size={18} weight="fill" className="text-[#FFD700]" />
                    </div>
                    <div>
                      <span className="font-bold font-['Outfit'] text-white">{t('vipCard')}</span>
                      <p className="text-[10px] text-white/40">{t('vipExplain')}</p>
                    </div>
                  </div>
                  {vipStatus.free_washes_available > 0 && (
                    <div className="px-3 py-1.5 rounded-full bg-[#FFD700] text-black text-sm font-bold shadow-[0_0_20px_rgba(255,215,0,0.4)]">
                      {vipStatus.free_washes_available} {t('freeBadge')}
                    </div>
                  )}
                </div>
                
                {/* Progress Section */}
                <div className="mb-3">
                  <div className="flex justify-between items-end mb-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold font-['Outfit'] text-white">
                        {vipStatus.progress_to_next_wash.toLocaleString()}
                      </span>
                      <span className="text-white/30 text-sm">/ {(vipStatus.target || 7000).toLocaleString()}</span>
                    </div>
                    <span className="text-[#0ABAB5] text-sm font-medium">
                      {Math.round(progressPercent)}%
                    </span>
                  </div>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full rounded-full"
                      style={{ 
                        background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.gold} 100%)`,
                        boxShadow: `0 0 10px ${BRAND.primaryGlow}`
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercent}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </div>
                </div>
                
                {isCloseToReward && vipStatus.free_washes_available === 0 && (
                  <p className="text-[#0ABAB5] text-sm font-medium flex items-center gap-1">
                    <Sparkle size={14} weight="fill" />
                    {t('vipMotivation')}
                  </p>
                )}
                {vipStatus.free_washes_available > 0 && (
                  <p className="text-[#FFD700] text-sm font-medium flex items-center gap-1">
                    <Crown size={14} weight="fill" />
                    {tn('freeWashesCount', vipStatus.free_washes_available)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Active Subscriptions — Iter17 Task 5: render ONE card per active sub
          (one per plate). Larje Pa Limit keeps the golden border; others use teal. */}
      {subscriptions.length > 0 && (
        <motion.div
          className="mx-4 mt-4 space-y-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          {subscriptions.map((sub) => sub.is_unlimited ? (
            <div key={sub.subscription_id} className="unlimited-gold-shell p-[2px] rounded-2xl" data-testid={`home-sub-${sub.subscription_id}`}>
              <div className="rounded-[14px] bg-[#0a0a0a] p-4 relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#FFD700]/15 blur-2xl pointer-events-none" />
                <div className="flex items-center justify-between mb-1 relative z-10">
                  <div className="min-w-0">
                    <p className="text-[#FFD700] text-[10px] font-bold uppercase tracking-[0.18em]">{t('currentPlan')}</p>
                    <p className="text-xl font-bold font-['Outfit'] text-white truncate">{sub.plan_name}</p>
                    {sub.license_plate && (
                      <p className="text-[11px] text-white/60 mt-0.5 font-mono tracking-wider" data-testid={`home-sub-plate-${sub.subscription_id}`}>
                        {sub.license_plate}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-extrabold text-[#FFD700] leading-none">∞</p>
                    <p className="text-[10px] text-white/45 mt-0.5">{t('unlimitedHint')}</p>
                  </div>
                </div>
                <p className="mt-2 text-[12px] text-white/85 font-medium tracking-wide relative z-10">
                  {t('unlimitedHomeTagline')}
                </p>
                {/* Iter20 Task 8 — subscription window display */}
                <p className="mt-2 text-[10px] text-white/55 tracking-wide relative z-10" data-testid={`home-sub-dates-${sub.subscription_id}`}>
                  {t('subscriptionWindowLabel')}: {fmtSubDate(sub.created_at)} → {fmtSubDate(sub.expires_at)}
                </p>
              </div>
            </div>
          ) : (
            <div key={sub.subscription_id} className="p-4 rounded-2xl bg-[#0a0a0a] border border-[#0ABAB5]/30" data-testid={`home-sub-${sub.subscription_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="min-w-0">
                  <p className="text-[#0ABAB5] text-[10px] font-bold uppercase tracking-wider">{t('currentPlan')}</p>
                  <p className="text-lg font-bold font-['Outfit'] text-white truncate">{sub.plan_name}</p>
                  {sub.license_plate && (
                    <p className="text-[11px] text-white/60 mt-0.5 font-mono tracking-wider" data-testid={`home-sub-plate-${sub.subscription_id}`}>
                      {sub.license_plate}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#0ABAB5]">
                    {sub.washes_remaining}
                  </p>
                  <p className="text-[10px] text-white/40">{t('washesRemaining')}</p>
                </div>
              </div>
              <div className="flex gap-1">
                {[...Array(Math.max(8, sub.washes_remaining))].map((_, i) => (
                  <div
                    key={`wash-slot-${i}`}
                    className={`flex-1 h-1.5 rounded-full transition-all ${
                      i >= sub.washes_remaining ? 'bg-white/20' : 'bg-[#0ABAB5]'
                    }`}
                  />
                ))}
              </div>
              {/* Iter20 Task 8 — subscription window display */}
              <p className="mt-2 text-[10px] text-white/55 tracking-wide" data-testid={`home-sub-dates-${sub.subscription_id}`}>
                {t('subscriptionWindowLabel')}: {fmtSubDate(sub.created_at)} → {fmtSubDate(sub.expires_at)}
              </p>
            </div>
          ))}
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div
        className="px-4 mt-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <h3 className="text-sm font-bold font-['Outfit'] text-white/60 uppercase tracking-wider mb-3">{t('quickActions')}</h3>
        <div className="grid grid-cols-3 gap-2">
          {quickActions.map((action, index) => (
            <Link
              key={action.path}
              to={action.path}
              className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all duration-300 active:scale-[0.97] ${
                action.primary 
                  ? 'bg-[#0ABAB5]/10 border-[#0ABAB5]/30' 
                  : 'bg-[#0a0a0a] border-white/5'
              }`}
              data-testid={`quick-action-${index}`}
            >
              <div 
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${action.color}20` }}
              >
                <action.icon size={22} weight="duotone" style={{ color: action.color }} />
              </div>
              <span className="text-[11px] text-white/70 text-center font-medium leading-tight">{action.label}</span>
            </Link>
          ))}
        </div>
      </motion.div>

      {/* Iter28 FIX 4 — "Zbulo Programet" row removed (redundant with "Fillo Tani" CTA above). */}
      <motion.div
        className="px-4 mt-5 space-y-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.35 }}
      >
        {/* Iter22 — Liquid Glass upsell banner (only when eligible) */}
        <DetailingUpsellBanner />

        {/* Iter23 G2 — Streak badge (visible only when streak ≥ 2 weeks) */}
        <StreakBadge />

        {/* Iter24 H1 — Premium Services section (Liquid Glass + Detailing i Plotë) */}
        <PremiumServicesSection />

        {/* Discover VIP Card */}
        <Link
          to="/vip-card"
          className="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-[#FFD700]/10 via-[#0ABAB5]/5 to-transparent border border-[#FFD700]/25 active:scale-[0.99] transition-transform relative overflow-hidden"
          data-testid="discover-vip-card-link"
        >
          {/* subtle shimmer */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_4s_infinite] pointer-events-none" />
          <div className="flex items-center gap-3 relative">
            <div className="w-10 h-10 rounded-xl bg-[#FFD700]/20 flex items-center justify-center">
              <Crown size={20} weight="fill" className="text-[#FFD700]" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">{t('discoverVipCard')}</p>
              <p className="text-xs text-white/40">{t('vipCardSubtitle')}</p>
            </div>
          </div>
          <ArrowRight size={20} className="text-white/40 relative" />
        </Link>

        {/* Iter23 G5 — Zbulo entry */}
        <Link to="/zbulo" data-testid="discover-zbulo-link"
              className="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-[#FF5A00]/10 via-[#FFD700]/5 to-transparent border border-[#FF5A00]/25 active:scale-[0.99] transition-transform">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF5A00]/20 flex items-center justify-center text-xl">✨</div>
            <div>
              <p className="text-sm font-medium text-white">Zbulo AutoSpa5</p>
              <p className="text-xs text-white/40">Video edukative</p>
            </div>
          </div>
          <ArrowRight size={20} className="text-white/40" />
        </Link>
      </motion.div>

      <div className="h-6" />
    </div>
  );
}
